import React, { useEffect, useState } from 'react'
import { Button, ButtonDiv, ConformityStatus, Content, LeftSideContainer, RightSide, StructureContainer, StructureHeader, Title } from './style'
import StructuringCertificateFields from './StructuringCertificateFields'
import axios from '../../../../config/axios'
import StructureColumnsInCertificate from './StructureColumns'
import saveImage from '../../../../assets/certificates/AddParameter/save.svg'
import { ParamType } from '../CreateParam'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { CertificateOutletType } from '..'
import view from '../../../../assets/global-assets/view.svg'
import { ColumnType } from '../../../../components/Modals/EditStructure'
import { Check, Info } from 'lucide-react'
import SupplierFile from '../../../../components/Certificates/SupplierFile'
import ImagesInTheCertificate from './ImagesInTheCertificate'
import HeaderFields from './HeaderFields'

interface ColumnsInStructureWithValues {
  id: number
  concatenatedId: number
  value: string
  name?: string
  isNew?: boolean
  unitUsed?: {id:number, name:string}
  possibleUnits?: {id:number, name:string}[]
}

interface ImageData {
  id?: number
  file?: File
  fileUrl?: string
  customName: string
  isEditing: boolean
  subtitle?: string
}

export interface CertificateType {
  param: {id:number, name:string, CertificateType?:{id:number,name:string}}
  columnsInStructureWithValues: ColumnsInStructureWithValues[]
  supplier: {id:number, companyName: string, isMain?: boolean}
  customer: {id:number, companyName: string}
  heatNumber: {id:number, heatNumber: string, originCertificateId?: number}
  date?: string
  description?: string
  startingMaterial?: string
  supplierFile?: File | string | null
  invoiceNumber?: string
  standard?: string
  heatTreatment?: string
  purchaseOrderNumber?: string
  observations?: string
  factory?: string
}

export const verifyConformity = (column: ColumnType, certificate:CertificateType) => {
  const columnValue:number | null = Number(certificate.columnsInStructureWithValues.find(cv => cv.id === column.id)?.value.replace(',', '.'))
  const hasMin = column.min !== undefined && column.min !== null
  const hasMax = column.max !== undefined && column.max !== null

  if (!hasMin && !hasMax) return true
  
  if (hasMin && hasMax) return Number(columnValue) >= Number(column.min) && Number(columnValue) <= Number(column.max)
  
  if (hasMin) return columnValue >= Number(column.min)
  
  if (hasMax) return columnValue <= Number(column.max)
  
  return true
}

const CreateCertificate = () => {
  const [ searchParam ] = useSearchParams()
  const id = searchParam.get('id')
  const navigate = useNavigate()
  const { setLoading } = useOutletContext<CertificateOutletType>()
  const [ images, setImages ] = useState<ImageData[]>([])
  const [ showRange, setShowRange ] = useState<number | null>(null)
  const [ certificate, setCertificate ] = useState<CertificateType>({
    param: {id:0, name:''},
    customer: {id: 0, companyName: ''},
    supplier: {id: 0, companyName: ''},
    heatNumber: {id: 0, heatNumber: ''},
    columnsInStructureWithValues: []
  })
  const bodyToSend = {
    paramId: certificate.param.id,
    customerCompanyId: certificate.customer.id,
    supplierCompanyId: certificate.supplier.id,
    heatNumberId: certificate.heatNumber.id,
    columnsInStructureWithValues: certificate.columnsInStructureWithValues,
    description: certificate.description,
    startingMaterial: certificate.startingMaterial,
    invoiceNumber: certificate.invoiceNumber,
    standard: certificate.standard,
    heatTreatment: certificate.heatTreatment,
    purchaseOrderNumber: certificate.purchaseOrderNumber,
    observations: certificate.observations,
    factory: certificate.factory,
    supplierFile: !(certificate.supplierFile instanceof File) ? certificate.supplierFile : undefined
  }
  const [ param, setParam ] = useState<ParamType>({
    id: 0,
    name: '',
    structures: []
  })

  useEffect(() => {
    if(id) {
      setLoading(true)
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${id}`)
        .then(response => {
          if(response.data) {
            const certificateData = response.data
            const { images, ...rest } = certificateData
            setCertificate(rest)
            
            if (images && images.length > 0) {
              const existingImages: ImageData[] = images.map((img: any) => ({
                id: img.id,
                fileUrl: img.fileUrl,
                customName: img.title,
                subtitle: img.subTitle,
                isEditing: false
              }))
              setImages(existingImages)
            }
          }
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [])
  
  useEffect(() => {
    if(certificate.param.id){
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/param/${certificate.param.id}`)
        .then(response => {
          if((response.data)) setParam(() => {
            const data:ParamType = response.data 
            return {
              id: data.id,
              name: data.name,
              structures: data.structures,
              type: data.type,
            }
          })
        })
        .catch(error => {
          console.error(error)
        })
    }
  }, [certificate.param.id])

  useEffect(() => {
    if(!id) {
      const columns: ColumnsInStructureWithValues[] = []
      param.structures.forEach(structure => {
        structure.columns.forEach((column: any) => {
          columns.push({
            id: column.id,
            value: '',
            concatenatedId: column.concatenatedId,
            name: column.name,
            possibleUnits: [],
            unitUsed: column.unitUsed
          })
        })
      })

      setCertificate(prev => ({
        ...prev,
        paramId: param.id || 0,
        columnsInStructureWithValues: columns
      }))
    }
  }, [param.id])
  
  useEffect(() => {
    if ((param.structures.length === 0 && certificate.columnsInStructureWithValues.length === 0) || location.pathname.includes('create')) return
    const newColumns: ColumnsInStructureWithValues[] = []
    
    param.structures.forEach(structure => {
      structure.columns.forEach((column: any) => {
        const existingColumn = certificate.columnsInStructureWithValues.find(cV => cV.id === column.id)
        
        if (!existingColumn) {
          newColumns.push({
            id: column.id,
            value: '',
            concatenatedId: column.concatenatedId,
            name: column.name,
            isNew: true
          })
        }
      })
    })
    
    if (newColumns.length > 0) {
      setCertificate(prev => ({
        ...prev,
        columnsInStructureWithValues: [...prev.columnsInStructureWithValues, ...newColumns]
      }))
    }
  }, [param.structures])

  const handleCreateEditCertificate = () => {
    setLoading(true)

    const formData = new FormData()
    
    images.forEach(imageData => {
      if (imageData.file) {
        formData.append('files', imageData.file)
      }
    })

    if(certificate.supplierFile instanceof File) {
      formData.append('supplierFile', certificate.supplierFile)
    }
    
    const imagesMetaData = images.map(iData => ({
      id: iData.id,
      customName: iData.customName,
      subtitle: iData.subtitle
    }))
    
    const bodyToSendWithImages = {
      ...bodyToSend,
      imagesMetaData
    }
    
    formData.append('json', JSON.stringify(bodyToSendWithImages))
    
    if(id) {
      axios.put(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${id}`, formData)
      .then(() => {
        navigate('..')
      })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          setLoading(false)
        })
      return
    }

    axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate`, formData)
      .then(() => {
        navigate('..')
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLoading(false)
      })
    return
  }
    
  useEffect(() => {
    if(!certificate.heatNumber.originCertificateId) return
    setLoading(true)
    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${certificate.heatNumber.originCertificateId}`)
    .then(response => {
        if(response.data) {
          setCertificate(prev => ({
            ...prev,
            supplierFile: response.data.supplierFile,
            factory: response.data.supplier.companyName,
            columnsInStructureWithValues: prev.columnsInStructureWithValues.map((column) => ({
              ...column,
              value: (response.data.columnsInStructureWithValues.find((item:ColumnsInStructureWithValues) => item.id === column.id) || column).value
            }))
          }))
        }
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => setLoading(false))
  }, [certificate.heatNumber.id])
  
  return (
    <Content>
      <LeftSideContainer>
        <StructuringCertificateFields certificate={certificate} setCertificate={setCertificate} />
        <SupplierFile certificate={certificate} setCertificate={setCertificate} />
      </LeftSideContainer>
      {
        certificate.param.id && certificate.customer.id && certificate.supplier.id ? (
          <RightSide>
            <HeaderFields certificate={certificate} setCertificate={setCertificate} />
            {param.structures.map((structure, index) => 
              <StructureContainer key={index}>
                <StructureHeader>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <Title>{structure.name}</Title>
                    {structure.columns.some(c => c.min || c.max) &&
                      <ConformityStatus outOfNorm={!structure.columns.every(c => verifyConformity(c, certificate))}>
                        {structure.columns.every(c => verifyConformity(c, certificate)) ? <><Check size={20} /> Valores conforme a norma</> : <><Info size={18} /> Algum valor está fora da norma </>}
                      </ConformityStatus>
                    }
                  </div>
                  {structure.columns.some(column => column.min !== null || column.max !== null) &&
                    <img
                      src={view}
                      alt='view-icon'
                      onMouseEnter={() => setShowRange(index)}
                      onMouseLeave={() => setShowRange(null)}
                    />
                  }
                </StructureHeader>
                <StructureColumnsInCertificate showRange={showRange} param={param} structureIndex={index} certificate={certificate} setCertificate={setCertificate} />
              </StructureContainer>
            )}
            <ImagesInTheCertificate images={images} setImages={setImages} />
          </RightSide>
        ) : null
      }
      <ButtonDiv>
        <Button onClick={() => navigate('..')}>Cancelar</Button>
        <Button onClick={handleCreateEditCertificate} isSave={true}> <img src={saveImage} /> Salvar certificado</Button>
      </ButtonDiv>
    </Content>
  )
}

export default CreateCertificate